// src/pages/admin/AdminUsersTab.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function AdminUsersTab() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState(null);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);

  const [userQuery, setUserQuery] = useState("");

  // VIN search state
  const [vinQuery, setVinQuery] = useState("");
  const [vinSearching, setVinSearching] = useState(false);
  const [vinSearchError, setVinSearchError] = useState(null);
  const [highlightedCompanyId, setHighlightedCompanyId] = useState(null);

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // Load all users (admin-only endpoint)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        setUsersError(null);

        const resp = await fetch("/api/dashboard/users/", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!resp.ok) {
          throw new Error("Error loading users");
        }

        const data = await resp.json();
        setUsers(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        setUsersError(err.message || "Unknown error");
      } finally {
        setLoadingUsers(false);
      }
    };

    if (token) {
      fetchUsers();
    } else {
      setLoadingUsers(false);
      setUsersError("No auth token");
    }
  }, [token]);

  // Load companies for selected user
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!selectedUserId) return;
      try {
        setLoadingCompanies(true);
        setCompaniesError(null);

        // Staff-юзер получает компании конкретного пользователя
        // через общий список компаний с фильтром по user_id.
        const resp = await fetch(`/api/companies/?user_id=${selectedUserId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!resp.ok) {
          throw new Error("Error loading companies for user");
        }

        const data = await resp.json();
        setUserCompanies(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        setCompaniesError(err.message || "Unknown error");
        setUserCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [selectedUserId, token]);

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const email = (u.email || "").toLowerCase();
      const idStr = String(u.id || "");
      const companies = (u.companies_names || []).join(" ").toLowerCase();
      const cards = (u.fee_card_last4s || []).join(" ");
      return (
        email.includes(q) ||
        idStr.includes(q) ||
        companies.includes(q) ||
        cards.includes(q)
      );
    });
  }, [users, userQuery]);

  const handleSelectUser = (userId) => {
    setSelectedUserId(userId);
    setHighlightedCompanyId(null); // сбрасываем подсветку при смене пользователя
  };

  const handleOpenCompany = (companyId) => {
    const vin = vinQuery.trim();
    if (vin && highlightedCompanyId === companyId) {
      // При переходе передаём VIN, чтобы на странице компании подсветить форму
      navigate(`/company/${companyId}?vin=${encodeURIComponent(vin)}`);
    } else {
      navigate(`/company/${companyId}`);
    }
  };

  // VIN search handler
  const handleVinSearch = async () => {
    const vin = vinQuery.trim();
    if (vin.length < 3) {
      setVinSearchError("Enter at least 3 characters");
      return;
    }

    try {
      setVinSearching(true);
      setVinSearchError(null);

      const resp = await fetch(
        `/api/companies/search-by-vin/?vin=${encodeURIComponent(vin)}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!resp.ok) {
        throw new Error("Error searching by VIN");
      }

      const data = await resp.json();

      if (!data || data.length === 0) {
        setVinSearchError("No companies found for this VIN");
        return;
      }

      // Берём первую найденную компанию и её владельца
      const company = data[0];
      const userId = company.user_id;
      const companyId = company.id;

      if (userId) {
        // Выбираем пользователя и подсвечиваем компанию
        setSelectedUserId(userId);
        setHighlightedCompanyId(companyId);
      } else {
        setVinSearchError("Company owner not found");
      }
    } catch (err) {
      setVinSearchError(err.message || "Unknown error");
    } finally {
      setVinSearching(false);
    }
  };

  const handleVinKeyDown = (e) => {
    if (e.key === "Enter") {
      handleVinSearch();
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-2xl font-semibold">Users</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
            placeholder="Search by email, company or card last4..."
            className="w-full sm:w-48 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
          />
          <div className="flex gap-1">
            <input
              type="text"
              value={vinQuery}
              onChange={(e) => {
                const value = e.target.value;
                setVinQuery(value);
                if (!value.trim()) {
                  // Очистка VIN-поиска возвращает полный список компаний
                  setHighlightedCompanyId(null);
                  setVinSearchError(null);
                }
              }}
              onKeyDown={handleVinKeyDown}
              placeholder="Search by VIN..."
              className="w-full sm:w-40 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
            />
            <button
              onClick={handleVinSearch}
              disabled={vinSearching}
              className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition disabled:opacity-50"
            >
              {vinSearching ? "..." : "Find"}
            </button>
          </div>
        </div>
      </div>
      {vinSearchError && (
        <div className="text-sm text-red-500">{vinSearchError}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users list */}
        <div className="border rounded overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 font-semibold text-sm border-b">
            All Users
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loadingUsers ? (
              <div className="p-4 text-sm text-gray-600">Loading users...</div>
            ) : usersError ? (
              <div className="p-4 text-sm text-red-500">{usersError}</div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No users found.</div>
            ) : (
              <table className="min-w-full text-xs sm:text-sm">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Balance</th>
                    <th className="px-3 py-2 text-left">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr
                      key={u.id}
                      onClick={() => handleSelectUser(u.id)}
                      className={`cursor-pointer border-b hover:bg-blue-50 ${
                        selectedUserId === u.id ? "bg-blue-50" : ""
                      }`}
                    >
                      <td className="px-3 py-2">{u.id}</td>
                      <td className="px-3 py-2">{u.email}</td>
                      <td className="px-3 py-2">
                        {typeof u.balance !== "undefined"
                          ? `$${u.balance}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {u.is_staff ? "Admin" : "User"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Companies of selected user */}
        <div className="border rounded overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 font-semibold text-sm border-b">
            {selectedUserId
              ? `Companies of user #${selectedUserId}`
              : "Select a user to see companies"}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {!selectedUserId ? (
              <div className="p-4 text-sm text-gray-500">
                Choose a user from the list on the left.
              </div>
            ) : loadingCompanies ? (
              <div className="p-4 text-sm text-gray-600">
                Loading companies...
              </div>
            ) : companiesError ? (
              <div className="p-4 text-sm text-red-500">{companiesError}</div>
            ) : userCompanies.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">
                No companies for this user.
              </div>
            ) : (
              <div className="divide-y">
                {userCompanies
                  .filter((c) =>
                    highlightedCompanyId ? c.id === highlightedCompanyId : true,
                  )
                  .map((c) => (
                    <div
                      key={c.id}
                      className={`p-3 cursor-pointer hover:bg-blue-50 ${
                        highlightedCompanyId === c.id
                          ? "bg-yellow-100 border-l-4 border-yellow-500"
                          : ""
                      }`}
                      onClick={() => handleOpenCompany(c.id)}
                    >
                      <div className="font-medium text-sm text-[#0F2C5E]">
                        {c.name}
                        {highlightedCompanyId === c.id && (
                          <span className="ml-2 text-xs text-yellow-700 bg-yellow-200 px-1 rounded">
                            VIN match
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        EIN: {c.EIN || "—"}
                      </div>
                      <div className="text-xs text-gray-600">
                        {c.state}, {c.city}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
